import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from 'react'
import type { ToolManagerRef } from '../ToolManager'
import { useProject } from '../hooks'
import { Shape, Vector2, Vector3, Vector3Like } from 'three'
import theme from '~/config/theme'
import { Circle, Line } from '@react-three/drei'
import { useAppDispatch, useAppSelector, useAppStore } from '~/store/hooks'
import { addComfortZone } from '~/store/objects'
import { guid } from '~/lib/utils'
import { uiToModel } from '../util/units'
import { GET_FACILITY_PRIMARY_USE_QUERY } from '~/client/queries'
import { useQuery } from '@apollo/client'
import { useParams } from 'react-router-dom'
import { primaryUses } from '~/config/facility'
import Util from '../lib/util'

const MINIMUM_THRESHOLD = 50

function useMakeComfortZone() {
  const zoneCount = useAppSelector(store => Object.keys(store.objects.present.comfortZones).length)
  const dispatch = useAppDispatch()
  const params = useParams<{ id: string }>()
  const { data } = useQuery(GET_FACILITY_PRIMARY_USE_QUERY, {
    variables: {
      id: params.id ?? '',
    },
  })

  return useCallback(
    (points: Vector3Like[], color: string) => {
      points = [...points, points[0]]
      const positions = points.map(pt => ({ x: uiToModel(pt.x), y: uiToModel(pt.y), z: uiToModel(pt.z) }))

      dispatch(
        addComfortZone({
          comfortZone: {
            id: guid(),
            color,
            positions: positions,
            primaryUse: data?.facility?.primaryUse ?? primaryUses[0].value,
            name: `Zone ${zoneCount + 1}`,
            position: Util.polygonCentroid(positions),
          },
        })
      )
    },
    [zoneCount]
  )
}

export const ComfortZoneRectTool = forwardRef<ToolManagerRef>(function ComfortZoneTool(_props, ref) {
  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const [pointOne, setPointOne] = useState<[x: number, y: number] | undefined>(undefined)
  const [color, _setColor] = useState(theme.colors.swatches[Math.floor(Math.random() * theme.colors.swatches.length)])
  const makeComfortZone = useMakeComfortZone()

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          setPoint([point.x, point.y])
        }
        return true
      },
      onMouseDown() {
        if (pointOne === undefined) {
          setPointOne([x, y])
        }

        return true
      },
      onMouseUp() {
        if (pointOne === undefined) {
          return true
        }

        const isTooSmall = new Vector2(x, y).distanceToSquared({ x: pointOne[0], y: pointOne[1] }) < MINIMUM_THRESHOLD
        if (isTooSmall) {
          setPointOne(undefined)
          return true
        }

        const minX = Math.min(x, pointOne[0])
        const maxX = Math.max(x, pointOne[0])
        const minY = Math.min(y, pointOne[1])
        const maxY = Math.max(y, pointOne[1])

        const points = [
          new Vector3(minX, minY, 0),
          new Vector3(minX, maxY, 0),
          new Vector3(maxX, maxY, 0),
          new Vector3(maxX, minY, 0),
        ]
        makeComfortZone(points, color)

        return true
      },
    }),
    [project, x, y, pointOne]
  )

  if (pointOne === undefined || (pointOne[0] === x && pointOne[1] === y)) {
    return <Circle position={new Vector3(x, y, 0)} args={[2, 20]} material-color="pink" />
  }

  const minX = Math.min(x, pointOne[0])
  const maxX = Math.max(x, pointOne[0])
  const minY = Math.min(y, pointOne[1])
  const maxY = Math.max(y, pointOne[1])

  const height = 48

  const points = [
    new Vector3(minX, minY, 0),
    new Vector3(minX, maxY, 0),
    new Vector3(maxX, maxY, 0),
    new Vector3(maxX, minY, 0),
  ]

  return (
    <>
      <Circle position={new Vector3(x, y, 0)} args={[2, 20]} material-color="pink" />

      <mesh>
        <extrudeGeometry
          args={[new Shape(points.map(pt => new Vector2().copy(pt))), { depth: height }]}
          attach="geometry"
        />
        <meshStandardMaterial color={color} transparent opacity={0.4} />
      </mesh>

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...points, points[0]]} color={color} dashed lineWidth={2} />
      </group>
      <Line points={[...points, points[0]]} color={color} dashed lineWidth={2} />
    </>
  )
})

function useMakeComfortZoneOnUnmount(points: Vector3[], color: string) {
  const makeComfortZone = useMakeComfortZone()
  const store = useAppStore()

  useEffect(
    () => () => {
      if (points.length < 3) {
        return
      }
      if (store.getState().tools.activeTool === 'COMFORT_ZONE_LINE_TOOL') {
        return
      }

      makeComfortZone(points, color)
    },
    [points, color, store]
  )
}

export const ComfortZoneCustomTool = forwardRef<ToolManagerRef>(function ComfortZoneCustomTool(_props, ref) {
  const project = useProject()
  const [[x, y], setPoint] = useState([0, 0])
  const [points, setPoints] = useState<Vector3[]>([])
  const [color, _setColor] = useState(theme.colors.swatches[Math.floor(Math.random() * theme.colors.swatches.length)])

  useMakeComfortZoneOnUnmount(points, color)

  useImperativeHandle(
    ref,
    () => ({
      onMouseMove(event) {
        const point = project(event)
        if (point) {
          setPoint([point.x, point.y])
        }
        return true
      },
      onMouseDown() {
        return true
      },
      onMouseUp() {
        setPoints(pts => [...pts, new Vector3(x, y, 0)])

        return true
      },
    }),
    [project, x, y]
  )

  if (points.length === 0) {
    return <Circle position={new Vector3(x, y, 0)} args={[2, 20]} material-color="pink" />
  }

  if (points.length < 3) {
    return (
      <>
        <Circle position={new Vector3(x, y, 0)} args={[2, 20]} material-color="pink" />
        <Line points={[...points, new Vector3(x, y, 0)]} color={color} dashed lineWidth={2} />
      </>
    )
  }

  const minX = points.reduce((prev, current) => Math.min(current.x, prev), +Infinity)
  const maxX = points.reduce((prev, current) => Math.max(current.x, prev), -Infinity)
  const minY = points.reduce((prev, current) => Math.min(current.y, prev), +Infinity)
  const maxY = points.reduce((prev, current) => Math.max(current.y, prev), -Infinity)

  const height = 48

  const boundingBoxPoints = [
    new Vector3(minX, minY, 0),
    new Vector3(minX, maxY, 0),
    new Vector3(maxX, maxY, 0),
    new Vector3(maxX, minY, 0),
    new Vector3(minX, minY, 0),
  ]

  const committedPoints = points
  const pendingPoints = [...points, new Vector3(x, y, 0)]

  return (
    <>
      <Circle position={new Vector3(x, y, 0)} args={[2, 20]} material-color="pink" />

      <mesh>
        <extrudeGeometry
          args={[new Shape(committedPoints.map(pt => new Vector2().copy(pt))), { depth: height }]}
          attach="geometry"
        />
        <meshStandardMaterial color={color} transparent opacity={0.4} />
      </mesh>

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...committedPoints, committedPoints[0]]} color={color} dashed lineWidth={2} />
      </group>
      <Line points={[...committedPoints, committedPoints[0]]} color={color} dashed lineWidth={2} />

      <group position={new Vector3(0, 0, height)}>
        <Line points={[...pendingPoints, pendingPoints[0]]} color={color} dashed dashSize={2} lineWidth={2} />
      </group>
      <Line points={[...pendingPoints, pendingPoints[0]]} color={color} dashed dashSize={2} lineWidth={2} />

      <group position={new Vector3(0, 0, height)}>
        <Line points={boundingBoxPoints} color="black" dashed lineWidth={2} />
      </group>
      <Line points={boundingBoxPoints} color="black" dashed lineWidth={2} />
    </>
  )
})
